import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import {
  contactsModalBodyClass,
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import {
  campaignAudienceActionBaseClass,
  campaignAudiencePrimaryActionClass,
} from "./CampaignManageRecipientsButton.js";
import { CampaignModalContent } from "./CampaignModalContent.js";
import { CampaignSendIcon } from "./CampaignSendIcon.js";

function SummaryRow({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="grid min-w-0 gap-1 px-4 py-3" data-testid={testId}>
      <dt className="text-[0.75rem] font-medium text-muted">{label}</dt>
      <dd className="m-0 grid min-w-0 justify-items-start gap-0.5">{children}</dd>
    </div>
  );
}

const valueClass = "m-0 max-w-full text-[0.875rem] font-semibold leading-snug text-foreground";
const secondaryClass = "m-0 max-w-full text-[0.75rem] leading-snug text-muted";

export function CampaignLaunchConfirmationDialog({
  open,
  deliveryMode,
  executableCount,
  excludedCount,
  requiredCredits,
  isUnlimited = true,
  senderName,
  senderPhone,
  templateName,
  schedule,
  timezone,
  launching,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  deliveryMode: "now" | "schedule";
  executableCount: number;
  excludedCount: number;
  requiredCredits?: number;
  isUnlimited?: boolean;
  senderName: string;
  senderPhone: string | null;
  templateName: string;
  /** Locale-formatted local date and time; null until a schedule is chosen. */
  schedule: { date: string; time: string } | null;
  timezone: string;
  launching: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const sendNow = deliveryMode === "now";
  const formatCount = (value: number) => new Intl.NumberFormat(i18n.language).format(value);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <CampaignModalContent
        className="w-[min(92vw,28rem)]"
        data-testid="campaign-launch-confirm-dialog"
        data-delivery-mode={deliveryMode}
      >
        <div className={contactsModalHeaderClass}>
          <div className="flex items-start gap-3">
            <span
              className="grid size-10 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface-muted text-foreground"
              aria-hidden="true"
            >
              {sendNow ? <CampaignSendIcon size={15} /> : <CalendarClock size={18} />}
            </span>
            <div className="grid min-w-0 gap-1 pt-0.5">
              <AlertDialogTitle>
                {sendNow
                  ? t("campaigns.review.launch.confirmSendTitle")
                  : t("campaigns.review.launch.confirmScheduleTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {sendNow
                  ? t("campaigns.review.launch.confirmSendHint")
                  : t("campaigns.review.launch.confirmScheduleHint")}
              </AlertDialogDescription>
            </div>
          </div>
        </div>
        <div className={cn(contactsModalBodyClass, "pb-5 pt-4")}>
          <dl className="m-0 grid divide-y divide-line/60 rounded-lg border border-line/70 bg-surface">
            <SummaryRow
              label={t("campaigns.review.checklist.audience")}
              testId="campaign-launch-confirm-audience"
            >
              <p className={valueClass} data-testid="campaign-launch-confirm-ready-count">
                {t("campaigns.review.launch.confirmWillReceive", {
                  count: executableCount,
                  formattedCount: formatCount(executableCount),
                })}
              </p>
              {excludedCount > 0 ? (
                <p className={secondaryClass} data-testid="campaign-launch-confirm-excluded-count">
                  {t("campaigns.review.checklist.audienceExcluded", {
                    count: formatCount(excludedCount),
                  })}
                </p>
              ) : null}
            </SummaryRow>

            <SummaryRow
              label={t("campaigns.review.checklist.sender")}
              testId="campaign-launch-confirm-sender"
            >
              <bdi className={cn(valueClass, "truncate")}>{senderName}</bdi>
              {senderPhone ? (
                <LtrValue className={cn(secondaryClass, "tabular-nums")}>{senderPhone}</LtrValue>
              ) : null}
            </SummaryRow>

            <SummaryRow
              label={t("campaigns.review.checklist.template")}
              testId="campaign-launch-confirm-template"
            >
              <bdi className={cn(valueClass, "break-all")}>{templateName}</bdi>
            </SummaryRow>

            <SummaryRow
              label={t("campaigns.review.checklist.credits")}
              testId="campaign-launch-confirm-credits"
            >
              <p className={valueClass}>
                {isUnlimited ? (
                  t("campaigns.review.credits.unlimited")
                ) : (
                  t("campaigns.review.credits.requiredCount", {
                    count: requiredCredits ?? executableCount,
                  })
                )}
              </p>
            </SummaryRow>

            <SummaryRow
              label={t("campaigns.review.checklist.delivery")}
              testId="campaign-launch-confirm-delivery"
            >
              {sendNow ? (
                <p className={valueClass}>{t("campaigns.review.launch.sendNowOption")}</p>
              ) : schedule ? (
                <>
                  <p className={valueClass}>
                    {schedule.date}
                    <span className="font-normal text-muted" aria-hidden="true">
                      {" · "}
                    </span>
                    <LtrValue className="tabular-nums">{schedule.time}</LtrValue>
                  </p>
                  <LtrValue className={secondaryClass}>{timezone}</LtrValue>
                </>
              ) : (
                <p className={valueClass}>{t("campaigns.review.launch.chooseDateTime")}</p>
              )}
            </SummaryRow>
          </dl>
        </div>
        <AlertDialogActions
          className={cn(contactsModalFooterClass, "max-sm:flex-col-reverse max-sm:items-stretch")}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(campaignAudienceActionBaseClass, "max-sm:h-10")}
            disabled={launching}
            onClick={() => onOpenChange(false)}
          >
            {t("campaigns.review.launch.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            className={cn(
              campaignAudiencePrimaryActionClass,
              "disabled:cursor-not-allowed max-sm:h-10",
            )}
            disabled={launching}
            onClick={onConfirm}
            data-testid="campaign-launch-confirm"
          >
            {launching ? (
              <>
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                {t("campaigns.review.launch.launching")}
              </>
            ) : sendNow ? (
              <>
                <CampaignSendIcon />
                {t("campaigns.review.launch.sendCampaign")}
              </>
            ) : (
              <>
                <CalendarClock size={14} aria-hidden="true" />
                {t("campaigns.review.launch.scheduleCampaign")}
              </>
            )}
          </Button>
        </AlertDialogActions>
      </CampaignModalContent>
    </AlertDialog>
  );
}
